<template>
  <!-- What we offer (features) in its five layouts (S_Variants). -->
  <SectionBand :heading="section.title" :variant="variant" :background="section.background" label="What we offer">
    <template v-if="section.description" #intro><p class="t-lead">{{ section.description }}</p></template>

    <div v-if="layout === 'tabs'" class="s2-ftabs">
      <div class="s2-ftabs__list" role="tablist" :aria-label="section.title || 'What we offer'">
        <button v-for="(f, i) in items" :key="i" type="button" role="tab" class="s2-ftabs__tab" :class="{on: i === active}"
                :aria-selected="i === active" :id="`${uid}-tab-${i}`" :aria-controls="`${uid}-panel-${i}`" :tabindex="i === active ? 0 : -1"
                @click="active = i" @keydown.right.prevent="move(1)" @keydown.left.prevent="move(-1)">{{ f.title }}</button>
      </div>
      <div v-for="(f, i) in items" v-show="i === active" :key="i" role="tabpanel" class="s2-ftabs__panel" :id="`${uid}-panel-${i}`" :aria-labelledby="`${uid}-tab-${i}`">
        <Media v-if="f.image" :media="f.image" ratio="16 / 9" />
        <p class="t-body">{{ f.description }}</p>
      </div>
    </div>

    <ol v-else-if="layout === 'list'" class="s2-flist mo-stagger">
      <li v-for="(f, i) in items" :key="i"><span class="t-eyebrow">{{ String(i + 1).padStart(2, '0') }}</span><strong class="t-h3">{{ f.title }}</strong><p class="t-muted">{{ f.description }}</p></li>
    </ol>

    <div v-else class="s2-fgrid mo-stagger" :data-layout="layout">
      <div v-for="(f, i) in items" :key="i" :class="layout === 'icons' ? 's2-ficon' : 't-card s2-fcard'">
        <Media v-if="layout === 'photo-cards' && f.image" :media="f.image" ratio="4 / 3" class="s2-fcard__photo" />
        <span v-else-if="showIcons" class="s2-fcard__icon" aria-hidden="true"><IconoirIcon :icon="f.icon">{{ icon(f.icon) }}</IconoirIcon></span>
        <h3 class="t-h3">{{ f.title }}</h3>
        <p class="t-muted">{{ f.description }}</p>
      </div>
    </div>
  </SectionBand>
</template>

<script setup>
import {computed, ref, useId} from "vue";
import SectionBand from "../shared/SectionBand.vue";
import Media from "../shared/Media.vue";
import IconoirIcon from "@/components/IconoirIcon.vue";

const props = defineProps({section: {type: Object, required: true}, variant: {type: [Number, String], default: 1}});
const layout = computed(() => (["cards", "list", "icons", "tabs", "photo-cards"].includes(props.section.layout) ? props.section.layout : "cards"));
const items = computed(() => (Array.isArray(props.section.features) ? props.section.features : []).filter((f) => f && (f.title || f.description)));
const showIcons = computed(() => props.section.showIcons !== false);
// Today's five keys draw as they always did; "iconoir:<name>" draws that icon (the key glyph is its fallback).
const ICONS = {star: "★", check: "✓", arrow: "→", lightbulb: "✦", dot: "•"};
const icon = (k) => ICONS[k] || "✓";
const active = ref(0);
const uid = useId();
function move(d) {
  const n = items.value.length;
  if (!n) return;
  active.value = (active.value + d + n) % n;
  document.getElementById(`${uid}-tab-${active.value}`)?.focus();
}
</script>

<style scoped>
.s2-fgrid { display: grid; gap: var(--t-gap); grid-template-columns: repeat(auto-fit, minmax(min(240px, 100%), 1fr)); }
/* Photo cards: one column on phones, never more than two, so the photos stay big. */
.s2-fgrid[data-layout="photo-cards"] { grid-template-columns: 1fr; }
@media (min-width: 640px) { .s2-fgrid[data-layout="photo-cards"] { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
.s2-fcard h3, .s2-ficon h3 { margin: .6rem 0 .35rem; }
.s2-fcard__icon { display: inline-flex; width: 2.4rem; height: 2.4rem; align-items: center; justify-content: center; border-radius: 999px; background: color-mix(in srgb, var(--t-accent) 14%, transparent); color: var(--t-accent); font-weight: 800; }
.s2-ficon { text-align: center; }
.s2-ficon .s2-fcard__icon { width: 3rem; height: 3rem; font-size: 1.3rem; }
.s2-fcard__icon :deep(.s2-glyph) { width: 1.35rem; height: 1.35rem; }
.s2-ficon .s2-fcard__icon :deep(.s2-glyph) { width: 1.6rem; height: 1.6rem; }
.s2-flist { list-style: none; padding: 0; margin: 0; }
.s2-flist li { display: grid; grid-template-columns: 3rem 1fr; column-gap: 1rem; padding: 1.1rem 0; border-top: 1px solid var(--t-line); }
.s2-flist li p { grid-column: 2; margin: .3rem 0 0; }
.s2-ftabs__list { display: flex; gap: .5rem; flex-wrap: wrap; margin-bottom: 1.25rem; }
.s2-ftabs__tab { border: 1.5px solid var(--t-line); background: var(--t-surface); color: var(--t-ink); border-radius: 999px; padding: .55rem 1.1rem; font: 600 .95rem var(--t-font-body); cursor: pointer; min-height: 44px; }
.s2-ftabs__tab.on { background: var(--t-primary); color: var(--t-primary-ink); border-color: var(--t-primary); }
.s2-ftabs__panel { display: grid; gap: 1rem; }
</style>
